// The draft-and-send state of the item view (DraftTranslate.dc.html; D55, D56, D57, D58,
// D59, D69, U13; B181). The draft is one voice.draft row read from GET /api/drafts/:id (the
// milestone's contract); the English tab is the user's to edit, and an edit posts one
// correction on blur (voice.correction, D56: corrections outrank statistics); the translated
// tab is what will be sent (D59, both versions kept); Send is a button here, and nothing
// leaves until it is pressed (D69). "Open in Outlook" reads the row's Outlook state and says
// "not in Outlook yet" while the grant that writes the copy is not held (D58, milestone 4
// open item 3). "How this sounds like you" is the register the draft was written in (D56),
// in the open, so a correction is one edit away.
//
// From Mike's live drive of 2026-09-16 (module 04 DESIGN B222, B223; nothing on the artboard
// names these, his instruction is their source): the copy list is drawn above the text, "To"
// and "Cc" with names, and a name in copy can be taken out (or one put in); a mail is replied
// to in its thread, never sent as a new mail; a Teams item is never mailed: the panel offers
// "Open in Teams" and "Copy the reply" and no Send. And while the panel waits on the server
// (a send, a translation, an edit, a copy-list change) the spinning Cobrain mark says what is
// happening and Send is disabled, so a second press cannot send twice; on a send the sent
// line is shown at once.

import { CardFrame } from './parts/primitives.js';
import { ActionInput, ActionButton, ActionTextarea, ActionSelect } from './parts/ActionButton.js';
import { viewFetch as fetch } from './view-as.js';
import { useEffect, useState } from 'react';
import { ApiError, Unauthenticated, drafts, teamsConsent, type Draft, type DraftRecipient, type DraftView, type VoiceNote } from './api.js';
import { channelOf as replyChannelOf, channelWords, NOTE_TO_WORDS, peopleWord, type ChannelWords, type ReplyChannel } from './reply/channel.js';
import { leanedWords, replyDrafts, type ReplyDraftView, type BookAsk } from './reply/api.js';
import { bookLabel, bookedWords } from './reply/offer.js';
import { browserZone } from './zone.js';
import { formatDate } from './labels.js';
import { Logo } from './Logo.js';

export const LANGUAGES: Record<string, string> = { en: 'English', fr: 'French', es: 'Spanish', it: 'Italian', de: 'German', pt: 'Portuguese', nl: 'Dutch' };
/** The language in words; nothing for a row that names none (found on the mock 2026-09-16: a render throw unmounts the page). */
export const languageWord = (code: string | null | undefined): string => (code ? LANGUAGES[code] ?? code.toUpperCase() : '');

/** The panel's words for the Outlook button, from the row (D58, U13). */
export function outlookWord(d: Pick<Draft, 'outlookDraftId'>): { label: string; there: boolean } {
  return d.outlookDraftId ? { label: 'Open in Outlook', there: true } : { label: 'Open in Outlook · not in Outlook yet', there: false };
}

/** "Register: vendor · contractions 12% · sentences 14 words · greeting Hi · sign-off Thanks · drawn from 214 of your own mails to vendors." */
export function voiceLine(v: VoiceNote): string {
  const parts = [`Register: ${v.register}`];
  if (v.contractionRate !== null) parts.push(`contractions ${Math.round(v.contractionRate * 100)}%`);
  if (v.medianSentenceWords !== null) parts.push(`sentences ${v.medianSentenceWords} words`);
  if (v.greeting) parts.push(`greeting “${v.greeting}”`);
  if (v.signOff) parts.push(`sign-off “${v.signOff}”`);
  // The API names the sample `examples` (Open 49); a missing figure reads as none rather than crashing the page
  // (found live on 2026-09-16: `drawnFrom` was undefined and the whole tree unmounted).
  const drawn = v.examples ?? v.drawnFrom ?? 0;
  parts.push(`drawn from ${Number(drawn).toLocaleString('en-GB')} of your own mails to ${v.register === 'internal' ? 'colleagues' : `${v.register}s`}`);
  return `${parts.join(' · ')}. Every edit you make here outranks the measured profile.`;
}

/** One name in the copy list: "Sam Partner <sam@partner.test>", or the address alone. */
export function recipientWord(r: DraftRecipient): string {
  return r.name ? `${r.name} <${r.address}>` : r.address;
}

export type Busy = 'sending' | 'translating' | 'saving' | 'copylist' | 'discarding' | null;

/** What the panel says beside the spinning mark while it waits (Mike, 2026-09-16: a text line alone is not obvious). */
export function busyWords(b: Exclude<Busy, null>, lang?: string): string {
  switch (b) {
    case 'sending': return 'Sending as you…';
    case 'translating': return `Translating${lang ? ` to ${languageWord(lang)}` : ''}…`;
    case 'saving': return 'Saving your edit as a correction…';
    case 'copylist': return 'Changing who is in copy…';
    case 'discarding': return 'Throwing it away…';
  }
}

/**
 * The channel a draft answers in. One table now (module 10 DESIGN B1555, `reply/channel.ts`): what
 * this file used to do was `v?.channel ?? 'email'`, so 'other' -- which is what every build before
 * 2026-09-22 wrote for a Cobrain messenger message and for a WhatsApp message -- read as EMAIL and
 * fell into the mail branch below. A chat message was drawn with a copy list and an Outlook button
 * and Send put it in the post.
 */
export const channelOf = replyChannelOf;

/** The heading: "Your reply to Sam", "Your reply in Teams to Will", "Your note". */
export function headingWord(channel: ReplyChannel, to: string | null, addressed = false): string {
  return wordsFor(channel, addressed).heading(to);
}

/** The channel's words; a note that goes to someone else reads as a mail to them (BUGS.md T26). */
export function wordsFor(channel: ReplyChannel, addressed: boolean | undefined): ChannelWords {
  return channel === 'none' && addressed === true ? NOTE_TO_WORDS : channelWords(channel);
}

/**
 * Who the heading and the Send button name. A mail names its first addressee, and the To line under the heading lists
 * the rest. A chat reply posts into the chat, where everyone in it reads it, so it names every one of them (module 04
 * DESIGN B1831; Mike, REQ-2026-09-24-46, "Name everyone": "Send it to Chris Watts and Renaldo Meere in Teams"). With
 * nobody named the draft's own `to` stands, as before.
 */
export function toWordOf(words: Pick<ChannelWords, 'mailFurniture'>, recipients: { to: DraftRecipient[]; others?: number } | null, fallback: string | null): string | null {
  if (!recipients || recipients.to.length === 0) return fallback;
  if (words.mailFurniture) return recipients.to[0]!.name ?? recipients.to[0]!.address;
  return peopleWord(recipients.to.map((r) => r.name ?? r.address), recipients.others ?? 0) ?? fallback;
}

/**
 * Puts the reply on the clipboard: the clipboard API when the browser allows it, else the older
 * selection-and-copy (found on 2026-09-16 driving the panel in the Browser pane, where the API is
 * refused without a permission). False when neither worked, so the panel can say so.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the selection copy */ }
  try {
    if (typeof document === 'undefined') return false;
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

// `context` is this flow's own field (module 10 DESIGN B1557) and `src/api.ts` belongs to another
// track, so it is added here rather than by editing that file.
type View = Omit<DraftView, 'draft' | 'voice'> & Pick<ReplyDraftView, 'context' | 'book' | 'booking'>;

/** The card option a draft answers (actions contract row 1): Send hands it over, and the card's decision is recorded once it has gone. */
export interface SentFrom { passItemId: string; optionKey: string }

/**
 * What the create call already said of the draft's channel (BUGS.md T64): given, the panel is drawn for that channel from its
 * first frame. Before, it was drawn for no channel until its own read came back, and a note showed "Send it" and "Open in
 * Outlook" for a moment before it turned into "Your note".
 */
export interface OpenedAs { channel?: string | null; addressed?: boolean; book?: BookAsk | null; recipients?: DraftView['recipients'] }

export function openedAsOf(r: unknown): OpenedAs | null {
  if (!r || typeof r !== 'object') return null;
  const o = r as { channel?: unknown; addressed?: unknown; book?: BookAsk | null; recipients?: DraftView['recipients'] };
  const out: OpenedAs = {};
  if (typeof o.channel === 'string') out.channel = o.channel;
  if (typeof o.addressed === 'boolean') out.addressed = o.addressed;
  if (o.book !== undefined) out.book = o.book;
  if (o.recipients !== undefined) out.recipients = o.recipients;
  return out.channel !== undefined || out.addressed !== undefined || out.book !== undefined ? out : null;
}

/** POST /api/drafts/:id/discard, "Throw it away" (BUGS.md T64): the draft discarded and its Outlook copy taken out. */
export function discardDraft(store: string, id: string): Promise<DraftView> {
  return fetch(`/api/drafts/${encodeURIComponent(id)}/discard`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store }) })
    .then(async (res) => {
      if (res.status === 401) throw new Unauthenticated();
      if (!res.ok) {
        const said = await res.json()
          .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
          .catch(() => null);
        throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
      }
      return (await res.json()) as DraftView;
    });
}

export function DraftPanel({ store, draftId, initial, from = null, opened = null, onClose, onSent }: { store: string; draftId: string; initial?: Draft | null; from?: SentFrom | null; opened?: OpenedAs | null; onClose?: () => void; onSent?: (d: Draft) => void }) {
  const [draft, setDraft] = useState<Draft | null>(initial ?? null);
  const [view, setView] = useState<View>(() => {
    const v: View = {};
    if (opened?.channel) v.channel = opened.channel as NonNullable<View['channel']>;
    if (opened?.addressed !== undefined) v.addressed = opened.addressed;
    if (opened?.book !== undefined) v.book = opened.book;
    if (opened?.recipients !== undefined) v.recipients = opened.recipients;
    return v;
  });
  const [voice, setVoice] = useState<VoiceNote | null>(null);
  const [tab, setTab] = useState<'en' | 'target'>('en');
  const [text, setText] = useState(initial?.bodyEn ?? '');
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  const [translateTo, setTranslateTo] = useState('');
  const [addCc, setAddCc] = useState('');
  const [copied, setCopied] = useState(false);
  // Milestone 6 check 6 (D202): the Send button appears only where the consent exists. Without it
  // the panel is exactly as it was -- Copy the reply and Open in Teams -- which is the path for
  // anyone Mike has not granted this for.
  const [canSendTeams, setCanSendTeams] = useState(false);
  const context = view.context ?? null;

  // Module 10 DESIGN B1559, Open 86 finding 2. This lived BELOW the two early returns from
  // 2026-09-16 to 2026-09-22, which is a conditional hook: the panel ran eight hooks while the
  // draft was loading and nine once it arrived, so React threw and the whole item page became
  // "This page failed to draw" -- taking with it the honest sentence one line above, which nobody
  // could ever have seen. It is a hook, so it goes with the hooks. `channel` is computed from the
  // state it reads, not from the render body, so moving it up costs nothing.
  const channel = channelOf(view);
  const teams = channel === 'teams';
  useEffect(() => {
    if (!teams) return;
    let cancelled = false;
    void teamsConsent()
      .then((c) => { if (!cancelled) setCanSendTeams(c.granted); })
      .catch(() => { if (!cancelled) setCanSendTeams(false); });
    return () => { cancelled = true; };
  }, [teams]);

  const take = (r: DraftView) => {
    const { draft: d, voice: v, ...rest } = r;
    setDraft(d); setVoice(v); setView((prev) => ({ ...prev, ...rest })); setText(d.bodyEn);
  };

  useEffect(() => {
    let cancelled = false;
    drafts.read(store, draftId)
      .then((r) => { if (!cancelled) take(r); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setLoadFailed(e instanceof Error ? e.message : String(e));
      });
    return () => { cancelled = true; };
  }, [store, draftId]);

  /**
   * One queued action. `saveFirst` writes the edit as a correction INSIDE the same action, before
   * the thing that needs it.
   *
   * Module 10 DESIGN B1558, and Open 86 finding 1 -- Mike's literal complaint that Send takes two
   * presses. Three things stacked up: the textarea saved its correction `onBlur`; `run` refuses a
   * second action while one is in flight; every button is disabled while one runs. A mouse press
   * takes focus off the textarea, so the blur-save started, disabled the button, and ATE THE PRESS.
   * Underneath it was worse: Send sent the body the server last stored, so an edit he never
   * blurred was never in the mail at all.
   *
   * Mended by construction and not by timing: the blur-save is gone, and the correction is the
   * first step of the action that needs it. Nothing races because there is only one action.
   */
  const run = async (what: Exclude<Busy, null>, fn: () => Promise<DraftView>, after?: (d: Draft) => void, saveFirst = false) => {
    if (busy) return;   // one thing at a time: a second press while the first is in flight does nothing
    setBusy(what); setError(null);
    try {
      if (saveFirst && draft && text !== draft.bodyEn && text.trim().length > 0) {
        // The correction still reaches the voice corpus on every path that mattered (D56, D57).
        take(await drafts.edit(draft.id, store, text));
      }
      const r = await fn();
      take(r); after?.(r.draft);
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (loadFailed) return <CardFrame variant="panel" className="draft-panel"><h2>Your draft</h2><p className="plain">Cobrain could not read the draft just now. {loadFailed}</p></CardFrame>;
  if (!draft) return <CardFrame variant="panel" className="draft-panel"><h2>Your draft</h2><div className="busy" role="status"><Logo spinning size={28} /><span className="note">Writing it in your voice…</span></div></CardFrame>;

  const lang = draft.targetLanguage ?? 'en';
  const translated = lang !== 'en';
  const outlook = outlookWord(draft);
  const words = wordsFor(channel, view.addressed);
  const sentAt = draft.sentAt ?? view.sent?.at ?? null;
  const sent = draft.status === 'sent' || view.state === 'sent' || view.state === 'sending' || Boolean(view.sent);
  const edited = text !== draft.bodyEn;
  const recipients = view.recipients ?? null;
  const toWord = toWordOf(words, recipients, draft.to);
  const sendText = translated && draft.bodyTarget ? draft.bodyTarget : text;
  const zone = browserZone() ?? 'UTC';
  const sendLabel = view.book ? bookLabel({ ...view.book, zone, late: view.book.hold }, view.book.minutes, [...(recipients?.to ?? []), ...(recipients?.cc ?? [])]) : words.sendLabel(toWord);
  const bookingWords = bookedWords(view.booking, zone);
  // What the draft leaned on, and what he asked for, read off the view (B1557). `context` is
  // absent from a row written before migration 174, and leanedWords draws that honestly rather
  // than drawing nothing.
  const leaned = leanedWords(context);
  const askedFor = context?.instruction ?? null;

  const copyReply = async () => {
    if (await copyText(sendText)) { setError(null); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    else setError('The reply could not be copied; select the text and copy it.');
  };

  return (
    <CardFrame variant="panel" className="draft-panel" data-draft={draft.id} data-status={draft.status} data-channel={channel}>
      <h2>{headingWord(channel, toWord, view.addressed)}</h2>
      {/* One line from the channel table, and NOT drawn once it has gone: a sent panel that still
          says "nothing is sent until you press the button" is one screen making two claims, which
          is what the cold review refused. */}
      {!sent ? <p className="rule">{words.rule}</p> : null}
      {words.mailFurniture && recipients && !sent ? (
        <div className="recipients">
          <p className="note"><b>To:</b> {recipients.to.map(recipientWord).join(', ')}</p>
          <p className="note"><b>Cc:</b>{' '}
            {recipients.cc.length === 0 ? <span>nobody in copy</span> : recipients.cc.map((r) => (
              <span key={r.address} className="cc">{recipientWord(r)}{!sent ? <ActionButton type="button" className="link inline" disabled={busy !== null} aria-label={`remove ${r.address} from copy`} onClick={() => void run('copylist', () => drafts.recipients(draft.id, store, { removeCc: r.address }))}>remove</ActionButton> : null}</span>
            ))}
            {!sent ? (
              <span className="addcc">
                <ActionInput aria-label="add someone in copy" placeholder="add someone in copy" value={addCc} disabled={busy !== null} onChange={(e) => setAddCc(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter' && addCc.includes('@')) { e.preventDefault(); void run('copylist', () => drafts.recipients(draft.id, store, { addCc }), () => setAddCc('')); } }} />
                <ActionButton type="button" className="link inline" disabled={busy !== null || !addCc.includes('@')} onClick={() => void run('copylist', () => drafts.recipients(draft.id, store, { addCc }), () => setAddCc(''))}>add</ActionButton>
              </span>
            ) : null}
          </p>
          {view.reply && !view.reply.inThread && view.reply.reason && channel === 'email' ? <p className="note">Not a reply in the thread: {view.reply.reason}.</p> : null}
        </div>
      ) : null}
      {/* "yours to edit" and "what will be sent" are both false once it has gone, so the tabs go
          with the rest of the send furniture; the sent words are drawn below the body instead. */}
      {!sent ? (
        <div className="tabs">
          <ActionButton reading type="button" className={tab === 'en' ? 'tab on' : 'tab'} onClick={() => setTab('en')}>English, yours to edit</ActionButton>
          {translated ? <ActionButton reading type="button" className={tab === 'target' ? 'tab on' : 'tab'} onClick={() => setTab('target')}>{languageWord(lang)}, what will be sent</ActionButton> : null}
        </div>
      ) : null}
      {tab === 'en' ? (
        sent ? <div className="draft">{draft.bodyEn}</div> : (
          <ActionTextarea className="draft" aria-label="draft in English" value={text} disabled={busy !== null} rows={Math.max(words.mailFurniture ? 6 : 3, text.split('\n').length + 1)}
                    onChange={(e) => setText(e.target.value)} />
        )
      ) : (
        draft.bodyTarget ? <div className="draft">{draft.bodyTarget}</div> : (
          <div className="draft"><span className="note">Not translated yet.</span> <ActionButton type="button" className="btn small" disabled={busy !== null} onClick={() => void run('translating', () => drafts.translate(draft.id, store, lang))}>Translate to {languageWord(lang)}</ActionButton></div>
        )
      )}
      {translated ? <p className="note">Translated to {languageWord(lang)} on send; both versions are kept, the {languageWord(lang)} for the record and the English for the corpus. A translated draft reads as a competent translation, not as you: the voice profile is language-specific.</p> : null}
      {busy ? <div className="busy" role="status" aria-live="polite"><Logo spinning size={28} /><span className="note">{busyWords(busy, lang)}</span></div> : null}

      {/* What it leaned on, BETWEEN the draft and Send (module 10 DESIGN B1557). Its job is to be
          read before he sends, not after: it is the difference between a mail he checks and a mail
          he scans. The first drawing of this block proved its own point -- the draft said "my
          flight leaves shortly after that" while the block said 18:05 against a 19:30 dinner, four
          inches apart on one screen. A draft that states a fact next to the fact it leaned on
          cannot quietly get it backwards. It is drawn on EVERY draft, empty or not, because an
          empty input that says nothing reads as a quiet day. */}
      {!sent ? (
        <CardFrame variant="panel" className="leaned">
          <h2>What this leaned on</h2>
          {leaned.facts.length > 0 ? <ul className="did">{leaned.facts.map((f) => <li key={f.id}>{f.line}</li>)}</ul> : null}
          <p className="note">{leaned.line}</p>
          {askedFor ? <p className="note">You asked for: “{askedFor}”</p> : null}
        </CardFrame>
      ) : null}

      {sent ? (
        /* A sent panel stops offering to send (B1557's second lesson, from the cold review): the
           first drawing kept a live yellow Send and an editable body beside "the sentence reached
           Ryan", one screen making both claims. It leads with what happened and carries nothing
           that would send again. */
        <div className="done">
          <p className="note sent">{words.cobrainSends ? 'Sent' : 'Kept'}{sentAt ? ` ${formatDate(sentAt)}` : ''}, by you{view.reply?.inThread ? ', as a reply in the thread' : ''}. The sent copy is marked as Cobrain’s draft so it never joins your voice corpus.</p>
          {bookingWords ? <p className="note" role="status">{bookingWords}</p> : null}
          {leaned.facts.length > 0 ? <p className="src">It leaned on: {leaned.facts.map((f) => f.line).join(' ')}</p> : null}
          {onClose ? <ActionButton reading type="button" className="link" onClick={onClose}>Close</ActionButton> : null}
        </div>
      ) : (
        <div className="send-row">
          {/* The one yellow goes to the thing that gets the reply where it is going. Where Cobrain
              cannot put it there -- WhatsApp, and the messenger until its send is wired -- that is
              the copy, and the rule line above has already said why. */}
          {words.cobrainSends && (channel !== 'teams' || canSendTeams) ? (
            <ActionButton type="button" className="btn" disabled={busy !== null || (translated && !draft.bodyTarget)}
                    onClick={() => void run('sending', () => replyDrafts.send(draft.id, store, translated ? lang : 'en', from), onSent, true)}>
              {translated ? `${sendLabel} in ${languageWord(lang)}` : sendLabel}
            </ActionButton>
          ) : null}
          {!words.cobrainSends || channel === 'teams' ? (
            /* The channel says what this button is: "Copy the reply" on a chat Cobrain cannot post
               to, "Copy the note" on a note. Teams is the one place it is a SECOND button beside a
               Send, so there it is always the reply that is copied. */
            <ActionButton type="button" className={words.cobrainSends && canSendTeams ? 'btn quiet' : 'btn'} disabled={busy !== null} onClick={() => void copyReply()}>{copied ? 'Copied' : channel === 'teams' ? 'Copy the reply' : words.sendLabel(toWord)}</ActionButton>
          ) : null}
          {channel === 'teams' ? (
            <ActionButton reading type="button" className="btn quiet" disabled={!view.teams?.webUrl} title={view.teams?.webUrl ? 'Opens the chat in Teams; paste the reply there' : 'The link to this chat is not on record'}
                    onClick={() => { if (view.teams?.webUrl) window.open(view.teams.webUrl, '_blank', 'noopener'); }}>Open in Teams</ActionButton>
          ) : null}
          {words.mailFurniture ? (
            <ActionButton type="button" className="btn quiet" disabled={!outlook.there} title={outlook.there ? 'The same draft sits in your Outlook drafts, so your phone works' : 'The Outlook copy is written once Cobrain holds the grant for it'}
                    onClick={() => { if (draft.outlookDraftId) window.open('https://outlook.office.com/mail/drafts', '_blank', 'noopener'); }}>{outlook.label}</ActionButton>
          ) : null}
          {!translated ? (
            <label className="note">Translate to{' '}
              <ActionSelect aria-label="translate to" value={translateTo} disabled={busy !== null} onChange={(e) => { setTranslateTo(e.target.value); if (e.target.value) void run('translating', () => drafts.translate(draft.id, store, e.target.value), () => setTab('target'), true); }}>
                <option value="">…</option>
                {Object.entries(LANGUAGES).filter(([c]) => c !== 'en').map(([c, w]) => <option key={c} value={c}>{w}</option>)}
              </ActionSelect>
            </label>
          ) : null}
          {/* He is not discarding a panel, he is throwing away a letter (the cold review). The edit
              is saved on the way out, so what he wrote still teaches the voice profile (D56), and then the
              draft is discarded on the server, its Outlook copy with it (BUGS.md T64): before, the panel only
              closed and the draft stayed on record. A refusal keeps the panel open with the server's words. */}
          {onClose ? <ActionButton type="button" className="link" disabled={busy !== null} onClick={() => { let ok = false; void run('discarding', async () => { const v = await discardDraft(store, draft.id); ok = true; return v; }, undefined, edited).then(() => { if (ok) onClose(); }); }}>Throw it away</ActionButton> : null}
        </div>
      )}
      {error ? <p className="error">{error}</p> : null}
      {voice ? (
        <div className="voice">
          <h2>How this sounds like you</h2>
          <p className="rule">The profile, in the open, so a correction is one edit away.{voice.isOnboarding ? ' This is one of the three drafts that confirm it.' : ''}</p>
          <p className="gist">{voiceLine(voice)}</p>
        </div>
      ) : null}
    </CardFrame>
  );
}
